import { useState, useEffect } from 'react';
import {
  usePromptTemplate,
  useUpdatePromptTemplate,
} from '../hooks/usePromptTemplate';

export function PromptTemplateEditor() {
  const { data: config, isLoading, error, refetch } = usePromptTemplate();
  const updateConfig = useUpdatePromptTemplate();

  const [promptName, setPromptName] = useState('');
  const [hasChanges, setHasChanges] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  useEffect(() => {
    if (config) {
      setPromptName(config.bedrockPromptName);
      setHasChanges(false);
    }
  }, [config]);

  const handlePromptNameChange = (value: string) => {
    setPromptName(value);
    setHasChanges(value !== config?.bedrockPromptName);
    setSaveError(null);
    setSaveSuccess(false);
  };

  const handleReset = () => {
    if (config) {
      setPromptName(config.bedrockPromptName);
      setHasChanges(false);
      setSaveError(null);
      setSaveSuccess(false);
    }
  };

  const validateInput = (): string | null => {
    if (!promptName.trim()) {
      return 'Bedrock prompt name is required';
    }
    // Basic format validation: should be alphanumeric with dashes, underscores, and optional :version suffix
    const pattern = /^[a-zA-Z0-9_-]+(:[a-zA-Z0-9]+)?$/;
    if (!pattern.test(promptName.trim())) {
      return 'Invalid prompt name format. Use alphanumeric characters, dashes, and underscores. Optional :version suffix allowed.';
    }
    return null;
  };

  const handleSave = async () => {
    const validationError = validateInput();
    if (validationError) {
      setSaveError(validationError);
      return;
    }

    try {
      await updateConfig.mutateAsync({
        bedrockPromptName: promptName.trim(),
      });
      setSaveSuccess(true);
      setHasChanges(false);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save prompt configuration');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-indigo-500 border-t-transparent" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-12 text-center">
        <p className="text-red-400">Failed to load prompt configuration</p>
        <button type="button" onClick={() => refetch()} className="btn-secondary mt-4">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Bedrock Prompt Configuration</h2>
          <p className="text-gray-400">
            Configure the AWS Bedrock prompt used to generate podcast scripts
          </p>
        </div>
        {config && (
          <p className="text-sm text-gray-500">
            Last updated: {formatDate(config.updatedAt)}
          </p>
        )}
      </div>

      {/* Info Box */}
      <div className="card border border-blue-500/30 bg-blue-500/10">
        <h3 className="mb-2 text-lg font-semibold text-blue-400">About Bedrock Prompt Management</h3>
        <p className="text-sm text-gray-300">
          Prompts are managed in the{' '}
          <a
            href="https://console.aws.amazon.com/bedrock/home#/prompt-management"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-400 hover:underline"
          >
            AWS Bedrock Console
          </a>
          . This allows you to:
        </p>
        <ul className="mt-2 space-y-1 text-sm text-gray-400">
          <li>- Edit prompts with a visual editor</li>
          <li>- Create versioned snapshots for rollback</li>
          <li>- Test prompts before deploying</li>
          <li>- Configure model and inference settings</li>
        </ul>
      </div>

      {/* Prompt Name Input */}
      <div className="card">
        <div className="space-y-4">
          <div>
            <label htmlFor="prompt-name" className="mb-2 block text-sm font-medium text-gray-300">
              Bedrock Prompt Name
            </label>
            <input
              id="prompt-name"
              type="text"
              value={promptName}
              onChange={(e) => handlePromptNameChange(e.target.value)}
              className="input"
              placeholder="e.g., kaster-podcast-script"
            />
            <p className="mt-2 text-sm text-gray-500">
              Enter the name of your prompt as it appears in AWS Bedrock Prompt Management.
            </p>
          </div>

          {/* Version Format Help */}
          <div className="rounded-md bg-gray-700/50 p-4">
            <h4 className="mb-2 text-sm font-medium text-gray-300">Version Format</h4>
            <div className="space-y-2 text-sm text-gray-400">
              <p>
                <code className="rounded bg-gray-800 px-2 py-0.5 text-green-400">my-prompt:latest</code>
                <span className="ml-2">Always uses the highest published version (recommended)</span>
              </p>
              <p>
                <code className="rounded bg-gray-800 px-2 py-0.5 text-indigo-400">my-prompt:3</code>
                <span className="ml-2">Uses specific published version 3</span>
              </p>
              <p>
                <code className="rounded bg-gray-800 px-2 py-0.5 text-yellow-400">my-prompt</code>
                <span className="ml-2">Uses the DRAFT version (for testing only)</span>
              </p>
            </div>
            <p className="mt-3 text-xs text-gray-500">
              Use <code className="text-green-400">:latest</code> for production - it automatically uses the newest published version.
            </p>
          </div>

          {/* Required Variables */}
          <div className="rounded-md bg-gray-700/50 p-4">
            <h4 className="mb-2 text-sm font-medium text-gray-300">Required Prompt Variables</h4>
            <p className="mb-2 text-sm text-gray-400">
              Your Bedrock prompt must include these variables using the <code className="rounded bg-gray-800 px-1 text-xs">{"{{variable}}"}</code> syntax:
            </p>
            <div className="space-y-1 text-sm">
              <p>
                <code className="rounded bg-gray-800 px-2 py-0.5 text-green-400">{"{{topic_name}}"}</code>
                <span className="ml-2 text-gray-400">- The name of the podcast topic</span>
              </p>
              <p>
                <code className="rounded bg-gray-800 px-2 py-0.5 text-green-400">{"{{style_guidance}}"}</code>
                <span className="ml-2 text-gray-400">- Style/tone guidance for the script</span>
              </p>
              <p>
                <code className="rounded bg-gray-800 px-2 py-0.5 text-green-400">{"{{content_summary}}"}</code>
                <span className="ml-2 text-gray-400">- The content with source citations</span>
              </p>
              <p>
                <code className="rounded bg-gray-800 px-2 py-0.5 text-green-400">{"{{recent_episodes_context}}"}</code>
                <span className="ml-2 text-gray-400">- Recent episodes for deduplication</span>
              </p>
            </div>
          </div>

          {/* Required JSON Response Schema */}
          <div className="rounded-md bg-gray-700/50 p-4">
            <h4 className="mb-2 text-sm font-medium text-gray-300">Required JSON Response Schema</h4>
            <p className="mb-3 text-sm text-gray-400">
              Your prompt must instruct the AI to return ONLY this JSON structure:
            </p>
            <pre className="overflow-x-auto rounded bg-gray-900 p-3 text-xs text-gray-300">
{`{
  "ssml": "<speak>Your SSML content with <mark name='ch1'/> chapter markers...</speak>",
  "chapters": [
    { "id": "ch1", "title": "Introduction", "description": "Opening remarks" },
    { "id": "ch2", "title": "Main Story", "description": "According to Crash.net..." }
  ],
  "sources": ["https://crash.net/article/...", "https://espn.com/..."],
  "short_summary": "Brief 1-2 sentence summary for deduplication"
}`}
            </pre>
            <div className="mt-3 space-y-2 text-xs text-gray-400">
              <p><strong className="text-gray-300">ssml:</strong> SSML with {"<mark name='chN'/>"} tags at chapter boundaries</p>
              <p><strong className="text-gray-300">chapters:</strong> Array of chapters matching the mark tags in SSML</p>
              <p><strong className="text-gray-300">sources:</strong> URLs of sources cited in the script</p>
              <p><strong className="text-gray-300">short_summary:</strong> Brief summary for avoiding duplicate content</p>
            </div>
          </div>

          {/* News Citation Format */}
          <div className="rounded-md bg-gray-700/50 p-4">
            <h4 className="mb-2 text-sm font-medium text-gray-300">Source Citation Format</h4>
            <p className="mb-2 text-sm text-gray-400">
              The content includes source names for proper citations. Instruct your prompt to use phrases like:
            </p>
            <ul className="space-y-1 text-sm text-gray-400">
              <li>• "According to <span className="text-blue-400">Crash.net</span>, Marquez made his return..."</li>
              <li>• "As reported by <span className="text-blue-400">ESPN</span>, the championship standings..."</li>
              <li>• "<span className="text-blue-400">Motorsport.com</span> reports that the team announced..."</li>
            </ul>
          </div>

          {/* Status Messages */}
          {saveError && (
            <div className="rounded-md bg-red-500/20 p-3 text-red-400">
              {saveError}
            </div>
          )}
          {saveSuccess && (
            <div className="rounded-md bg-green-500/20 p-3 text-green-400">
              Prompt configuration saved successfully!
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 border-t border-gray-700 pt-4">
            {hasChanges && (
              <span className="mr-auto text-sm text-yellow-400">
                You have unsaved changes
              </span>
            )}
            <button
              type="button"
              onClick={handleReset}
              disabled={!hasChanges || updateConfig.isPending}
              className="btn-secondary"
            >
              Discard Changes
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={!hasChanges || updateConfig.isPending}
              className="btn-primary"
            >
              {updateConfig.isPending ? 'Saving...' : 'Save Configuration'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
